/* eslint-disable indent */
import React, { lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/nextjs";
import { ImageIcon, Music, UploadCloudIcon, Video } from "lucide-react";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Loader } from "@/components/atoms/Loader";
import ImageForm, { ImageFormRef } from "@/components/molecules/GenerateForm/ImageForm";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { deleteImage, deletePreset, onToggleVisibility } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { useEditImage, useImageVoting } from "@/lib/hooks";
import { AppReducerActionType, ImageDTO, IPresetDTO } from "@/types";
import { LOADING_TEXT } from "@/utils/promptConstants";
import { ImageViewer } from "components/molecules/ImageViewer";
const PresetSidebar = lazy(() => import("components/molecules/PresetSidebar").then(module => ({
  default: module.PresetSidebar
})));
const LightboxWithMetadata = lazy(() => import("components/molecules/Lightbox").then(module => ({
  default: module.LightboxWithMetadata
})));
const PresetSubscriptionModal = lazy(() => import("components/molecules/PresetSubscriptionModal").then(module => ({
  default: module.PresetSubscriptionModal
})));
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import * as Styled from "./GenerateForm.styled";
const AudioForm = dynamic(() => import("@/components/molecules/GenerateForm/AudioForm"), {
  ssr: false
});
const VideoForm = dynamic(() => import("@/components/molecules/GenerateForm/VideoForm"), {
  ssr: false
});
const UploadForm = dynamic(() => import("@/components/molecules/GenerateForm/UploadForm"), {
  ssr: false
});
const SkeletonForm = () => <div className="w-full p-6 bg-card rounded-lg shadow-lg border border-border space-y-6 md:!w-[968px] md:!h-[1025px]" data-sentry-component="SkeletonForm" data-sentry-source-file="index.tsx">
        {/* Header */}
        <div className="space-y-2">
            <Skeleton className="h-8 w-[300px] mx-auto" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            <Skeleton className="h-4 w-[500px] mx-auto" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>

        {/* Quick/Custom Generate Buttons */}
        <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            <Skeleton className="h-12" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>

        {/* Model Selection */}
        <div className="space-y-4">
            <div className="flex items-center space-x-4">
                <Skeleton className="h-16 w-16" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Model image */}
                <div className="space-y-2 flex-1">
                    <Skeleton className="h-6 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Model name */}
                    <Skeleton className="h-4 w-64" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Model description */}
                </div>
            </div>
            <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Model dropdown */}
        </div>

        {/* Settings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Image Size */}
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            </div>
            {/* Number of Images */}
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            </div>
        </div>

        {/* Premium Content Switch */}
        <div className="flex items-center space-x-2">
            <Skeleton className="h-6 w-6" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
        </div>

        {/* Art Style and Theme */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            </div>
            <div className="space-y-2">
                <Skeleton className="h-4 w-24" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
                <Skeleton className="h-10 w-full" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" />
            </div>
        </div>

        {/* Collapsible Sections */}
        {[...Array(4)].map((_, i) => <div key={i} className="space-y-2">
                <Skeleton className="h-12 w-full" />
            </div>)}

        {/* Footer */}
        <div className="flex justify-between items-center pt-4">
            <Skeleton className="h-10 w-[120px]" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Save Preset */}
            <div className="flex items-center space-x-4">
                <Skeleton className="h-6 w-32" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Total Gold Cost */}
                <Skeleton className="h-10 w-[140px]" data-sentry-element="Skeleton" data-sentry-source-file="index.tsx" /> {/* Generate Button */}
            </div>
        </div>
    </div>;
export default function Component() {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();
  const [activeContentType, setActiveContentType] = useState("image");
  const [loading, setLoading] = useState<boolean>(false);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [error, setError] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isSavePresetModalOpen, setIsSavePresetModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [presets, setPresets] = useState<IPresetDTO[]>([]);
  const imageFormRef = useRef<ImageFormRef>(null);
  const contentTypes = [{
    id: "image",
    name: "Image",
    icon: ImageIcon,
    title: "Image Generation",
    description: "Create stunning images with AI-powered generation tools."
  }, {
    id: "video",
    name: "Video",
    icon: Video,
    title: "Video Creation",
    description: "Produce engaging videos with advanced AI assistance."
  }, {
    id: "audio",
    name: "Audio",
    icon: Music,
    title: "Audio Production",
    description: "Create immersive audio experiences with AI-powered tools."
  }, {
    id: "upload",
    name: "Upload",
    icon: UploadCloudIcon,
    title: "Upload Content",
    description: "Upload your own content for AI-powered processing."
  }];
  function renderActiveForm() {
    switch (activeContentType) {
      case "image":
        return <ImageForm ref={imageFormRef} loading={loading} setLoading={setLoading} setError={setError} presets={presets} setPresets={setPresets} onGenerateImages={handleSetImages} isSavePresetModalOpen={isSavePresetModalOpen} setIsSavePresetModalOpen={setIsSavePresetModalOpen} />;
      case "video":
        return <Suspense fallback={<SkeletonForm />}>
                        <VideoForm loading={loading} setLoading={setLoading} setError={setError} onGenerateImages={handleSetImages} />
                    </Suspense>;
      case "audio":
        return <Suspense fallback={<SkeletonForm />}>
                        <AudioForm loading={loading} setLoading={setLoading} setError={setError} onGenerateImages={handleSetImages} />
                    </Suspense>;
      case "upload":
        return <Suspense fallback={<SkeletonForm />}>
                        <UploadForm loading={loading} setLoading={setLoading} setError={setError} onGenerateImages={handleSetImages} />
                    </Suspense>;
      default:
        return null;
    }
  }
  const updateImage = (updatedImage: ImageDTO) => {
    setImages(prevImages => {
      return prevImages.map(image => image.id === updatedImage.id ? updatedImage : image);
    });
  };
  const handleSetImages = (response: string | ImageDTO[]) => {
    try {
      if (typeof response === "string") {
        setError("Invalid response format");
      } else if (Array.isArray(response)) {
        setImages(response);
      } else {
        setImages([response]);
      }
    } catch (error: any) {
      captureException("Error parsing response:", error);
    }
  };
  const hasResponses = loading || images.length > 0;
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  };
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(images[newIndex]);
  };
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const {
    handleVote
  } = useImageVoting(images, selectedImage, setImages, setSelectedImage);
  const {
    handleEditSubmit
  } = useEditImage(setIsLightboxOpen, handleSetImages, setLoading, setError);
  const handleConfirmDelete = async (imageId?: string) => {
    if (!imageId) return;
    try {
      await deleteImage(imageId);
      const newImageDtos = images.filter(img => img.id !== imageId);
      setImages(newImageDtos);
    } catch (error: any) {
      captureException("Failed to delete image", error);
    }
  };
  const handleDeletePreset = async (presetId: string) => {
    await deletePreset(presetId);
    setPresets(prevPresets => prevPresets.filter(preset => preset.id !== presetId));
    toast.success("Preset deleted successfully!", {
      position: "bottom-center"
    });
  };
  const handleToggle = (imageId: string) => {
    const currentVisibility = images.find(img => img.id === imageId)?.public;
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setImages(prev => prev.map(image => image.id === imageId ? {
        ...image,
        public: newVisibility
      } : image));
    });
  };
  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.contentType) {
      setActiveContentType(router.query.contentType as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  return <>
            {state.isLoggedIn && <Suspense fallback={null}>
                    <PresetSidebar presets={presets} onClick={p => imageFormRef.current?.handleApplyPreset(p)} onDelete={handleDeletePreset} />
                </Suspense>}
            <Styled.ScreenWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.InnerContainer hasresponses={hasResponses} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.StyledCard $hasResponses={hasResponses} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.GenerationTypesTabsList data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {contentTypes.map(type => <Button key={type.id} onClick={() => setActiveContentType(type.id)} className={`flex items-center p-2 rounded-md ${activeContentType === type.id ? "!bg-[#4F80CC] !text-[#FFFFFF] hover:!bg-[#3D6EB8]" : "!bg-[#2C2E52] !text-[#E8EAF0] hover:!bg-[#3D4066]"} border border-slate-600/20 transition-colors duration-200`}>
                                    <type.icon className="w-5 h-5 mr-1" />
                                    {type.name}
                                    {(type.name === "Audio" || type.name === "Video") && <div className="w-fit bg-gradient-to-r from-pink-600 to-purple-600 text-[10px] text-white px-2 py-1 rounded-full shadow-lg animate-pulse">
                                            New
                                        </div>}
                                </Button>)}
                        </Styled.GenerationTypesTabsList>
                        <Styled.StyledCardHeader data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.StyledCardTitle data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                CharGen: AI{" "}
                                {activeContentType.charAt(0).toUpperCase() + activeContentType.slice(1).toLowerCase()}{" "}
                                Generator
                            </Styled.StyledCardTitle>
                            <Styled.StyledCardDescription data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                Generate fantasy {activeContentType} for your
                                characters, NPCs, monsters, items, landscapes,
                                and battlemaps.
                            </Styled.StyledCardDescription>
                        </Styled.StyledCardHeader>
                        {renderActiveForm()}
                    </Styled.StyledCard>
                    {error && <div>
                            <Alert variant="destructive" className="flex items-center space-x-2 p-2 max-w-xs mx-auto">
                                <AlertTitle className="font-semibold">
                                    Error
                                </AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        </div>}
                    {hasResponses && <Styled.ImageViewerContainer $hasImages={hasResponses}>
                            <div className="content">
                                {loading && <Loader prompts={LOADING_TEXT} />}
                                <ImageViewer imageDtos={images} onImageClick={handleImageClick} onVote={handleVote} updateImage={updateImage} onSubmitEdit={handleEditSubmit} />
                            </div>
                        </Styled.ImageViewerContainer>}
                </Styled.InnerContainer>
            </Styled.ScreenWrapper>
            {isLightboxOpen && selectedImage && <Suspense fallback={null}>
                    <LightboxWithMetadata imageDtos={images} initialImageId={selectedImage.id} onClose={handleLightboxClose} open={isLightboxOpen} onToggleVisibility={handleToggle} onVote={handleVote} onIndexChange={handleIndexChange} handleConfirmDelete={handleConfirmDelete} />
                </Suspense>}
            {isSubscriptionModalOpen && <Suspense fallback={null}>
                    <PresetSubscriptionModal isOpen={isSubscriptionModalOpen} onCancel={() => setIsSubscriptionModalOpen(false)} />
                </Suspense>}
        </>;
}